import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { usePortalContainer } from "../_overlay/PortalHost.js";
import { IconButton } from "../IconButton/IconButton.js";
/* empty css          */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { createPortal } from "react-dom";
//#region src/components/Toast/Toast.tsx
var TONE_ICON = {
	info: "info",
	success: "check-circle",
	warning: "alert-triangle",
	danger: "alert-circle"
};
var ToastContext = createContext(null);
/** The plain text of a node, for the screen-reader announcement. */
function textOf(node) {
	if (node == null || typeof node === "boolean") return "";
	if (typeof node === "string" || typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(textOf).join("");
	if (typeof node === "object" && "props" in node) return textOf(node.props.children);
	return "";
}
/**
* Hosts toasts for the app: wrap it (inside the ThemeRoot) once, and call `useToast()`
* anywhere below. Toasts never take focus; they are announced (politely, or assertively
* for warnings and errors), pause while hovered or focused, and the hotkey (F8) moves
* focus to the newest one.
*/
function ToastProvider({ children, position = "bottom-right", hotkey = "F8", label = "Notifications" }) {
	const [toasts, setToasts] = useState([]);
	const [announcement, setAnnouncement] = useState({
		polite: "",
		assertive: ""
	});
	const counter = useRef(0);
	const timers = useRef(/* @__PURE__ */ new Map());
	const paused = useRef(false);
	const regionRef = useRef(null);
	const returnFocus = useRef(null);
	const dismiss = useCallback((id) => {
		const timer = timers.current.get(id);
		if (timer?.handle) clearTimeout(timer.handle);
		timers.current.delete(id);
		setToasts((list) => list.filter((record) => record.id !== id));
	}, []);
	const schedule = useCallback((id, ms) => {
		const handle = paused.current ? void 0 : setTimeout(() => dismiss(id), ms);
		timers.current.set(id, {
			handle,
			ends: Date.now() + ms,
			left: ms
		});
	}, [dismiss]);
	const toast = useCallback((options) => {
		const id = ++counter.current;
		const tone = options.tone ?? "info";
		const duration = options.duration ?? (tone === "warning" || tone === "danger" ? 0 : 4500);
		setToasts((list) => [...list, {
			...options,
			id,
			tone,
			duration
		}]);
		const text = [textOf(options.title), textOf(options.description)].filter(Boolean).join(". ");
		const key = tone === "warning" || tone === "danger" ? "assertive" : "polite";
		setAnnouncement((previous) => ({
			...previous,
			[key]: previous[key] === text ? `${text}\u00a0` : text
		}));
		if (duration > 0) schedule(id, duration);
		return id;
	}, [schedule]);
	const pause = useCallback(() => {
		if (paused.current) return;
		paused.current = true;
		for (const timer of timers.current.values()) {
			if (timer.handle) clearTimeout(timer.handle);
			timer.handle = void 0;
			timer.left = Math.max(0, timer.ends - Date.now());
		}
	}, []);
	const resume = useCallback(() => {
		if (!paused.current) return;
		paused.current = false;
		for (const [id, timer] of timers.current) schedule(id, timer.left);
	}, [schedule]);
	useEffect(() => {
		const onKeyDown = (event) => {
			if (event.key !== hotkey || event.defaultPrevented) return;
			const toastsInRegion = regionRef.current?.querySelectorAll(".ds-toast");
			const newest = toastsInRegion?.[toastsInRegion.length - 1];
			if (!newest) return;
			event.preventDefault();
			returnFocus.current = document.activeElement;
			newest.focus();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [hotkey]);
	useEffect(() => () => {
		for (const timer of timers.current.values()) if (timer.handle) clearTimeout(timer.handle);
	}, []);
	const api = useMemo(() => ({
		toast,
		dismiss
	}), [toast, dismiss]);
	const container = usePortalContainer();
	const close = (id) => {
		const focusWasInside = regionRef.current?.contains(document.activeElement) ?? false;
		dismiss(id);
		if (focusWasInside) {
			const back = returnFocus.current;
			if (back && back.isConnected) back.focus();
			resume();
		}
	};
	const region = /* @__PURE__ */ jsxs("div", {
		className: cx("ds-toast-region", `ds-toast-region--${position}`),
		"data-ds-toast-region": "",
		children: [
			/* @__PURE__ */ jsx(VisuallyHidden, {
				role: "status",
				children: announcement.polite
			}),
			/* @__PURE__ */ jsx(VisuallyHidden, {
				role: "alert",
				children: announcement.assertive
			}),
			toasts.length > 0 && /* @__PURE__ */ jsx("ol", {
				ref: regionRef,
				className: "ds-toast-list",
				"aria-label": label,
				onPointerEnter: pause,
				onPointerLeave: resume,
				onFocus: pause,
				onBlur: (event) => {
					if (!event.currentTarget.contains(event.relatedTarget)) resume();
				},
				children: toasts.map((record) => /* @__PURE__ */ jsxs("li", {
					tabIndex: -1,
					className: cx("ds-toast", `ds-toast--${record.tone}`),
					onKeyDown: (event) => {
						if (event.key === "Escape") {
							event.preventDefault();
							close(record.id);
						}
					},
					children: [
						/* @__PURE__ */ jsx(Icon, {
							name: TONE_ICON[record.tone],
							className: "ds-toast__icon"
						}),
						/* @__PURE__ */ jsxs("div", {
							className: "ds-toast__content",
							children: [
								/* @__PURE__ */ jsx("p", {
									className: "ds-toast__title",
									children: record.title
								}),
								record.description != null && /* @__PURE__ */ jsx("p", {
									className: "ds-toast__description",
									children: record.description
								}),
								record.action && /* @__PURE__ */ jsx("button", {
									type: "button",
									className: "ds-toast__action",
									onClick: () => {
										record.action?.onClick();
										close(record.id);
									},
									children: record.action.label
								})
							]
						}),
						/* @__PURE__ */ jsx(IconButton, {
							icon: "x",
							size: "sm",
							"aria-label": "Dismiss notification",
							className: "ds-toast__close",
							onClick: () => close(record.id)
						})
					]
				}, record.id))
			})
		]
	});
	return /* @__PURE__ */ jsxs(ToastContext.Provider, {
		value: api,
		children: [children, container && createPortal(region, container)]
	});
}
/** Shows toasts from anywhere under a ToastProvider: `const { toast } = useToast()`. */
function useToast() {
	const api = useContext(ToastContext);
	if (!api) throw new Error("useToast must be used within a <ToastProvider>");
	return api;
}
//#endregion
export { ToastProvider, useToast };

//# sourceMappingURL=Toast.js.map